import { includes, uniq } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { FullFacet } from "../facets";
import { GeoTerm, GeoTerms, getTermFromAggs, isGeoTerm } from "../search";
import {
  isClassificationCard,
  isDateMetricCard,
  isDateMetricsCard,
  isDateTermCard,
  isDemographicCard,
  isGeoCard,
  isPivotCard,
  isTermCard,
  isTermMetricsCard,
} from "./is";
import { Card } from ".";

export const demographicId = "__demographic__";

export const globalVolumeAndKPIId = "__global-volume-and-kpi__";

export const TermCardTerms = [
  Facet.Affects,
  Facet.ImageRecognitionAge,
  Facet.Genders,
  Facet.ImageRecognitionPersons,
  Facet.Devices,
  Facet.EmojiCharts,
  Facet.ImageRecognitionEmotions,
  Facet.Emotions,
  Facet.Focuses,
  Facet.ImageRecognitionGenders,
  Facet.Hashtags,
  Facet.Languages,
  Facet.Mentions,
  Facet.Entities,
  Facet.Keyphrases,
  Facet.ImageRecognitionObjects,
  Facet.ImageRecognitionScenes,
  Facet.Occupations,
  Facet.Platforms,
  Facet.Tones,
  Facet.Occupations,
  Facet.Topics,
  Facet.ReviewRate,
  Facet.Product,
  Facet.ProductRange,
  Facet.Franchise,
  Facet.SignificantEntities,
  Facet.SignificantHashtags,
  Facet.SignificantMentions,
  Facet.SignificantNamedEntities,
  Facet.EmergingHashtags,
  Facet.EmergingMentions,
  Facet.EmergingNamedEntities,
  Facet.SignificantKeyphrases,
  Facet.EmergingKeyphrases,
  Facet.ImageRecognitionLogos,
  Facet.ImageRecognitionPersonIdentifications,
  Facet.ContentClassification,
] as const;

export type TermCardTerm = typeof TermCardTerms[number];

export const PivotCardPivotTerms = [
  Facet.Genders,
  Facet.Genders,
  Facet.ImageRecognitionAge,
  Facet.ImageRecognitionPersons,
  Facet.Devices,
  Facet.Emotions,
  Facet.ImageRecognitionEmotions,
  Facet.Focuses,
  Facet.ImageRecognitionGenders,
  Facet.Languages,
  Facet.Platforms,
  Facet.Tones,
  Facet.ReviewRate,
  Facet.Countries,
] as const;

export type PivotCardTerm = typeof PivotCardPivotTerms[number];

export const DateTermCardTerms = [
  Facet.Genders,
  Facet.ImageRecognitionAge,
  Facet.ImageRecognitionPersons,
  Facet.Devices,
  Facet.Emotions,
  Facet.Focuses,
  Facet.ImageRecognitionGenders,
  Facet.ImageRecognitionEmotions,
  Facet.Languages,
  Facet.Platforms,
  Facet.Tones,
  Facet.ReviewRate,
  Facet.Hashtags,
  Facet.Keyphrases,
] as const;

export type DateTermCardTerm = typeof DateTermCardTerms[number];

export type GeoCardTerm = GeoTerm;

export const TermMetricsCardTerms = [
  Facet.Genders,
  Facet.ImageRecognitionAge,
  Facet.ImageRecognitionPersons,
  Facet.Devices,
  Facet.Emotions,
  Facet.Focuses,
  Facet.ImageRecognitionGenders,
  Facet.ImageRecognitionEmotions,
  Facet.Languages,
  Facet.Platforms,
  Facet.Tones,
] as const;

export type TermMetricsCardTerm = typeof TermMetricsCardTerms[number];

export const CardTerms = uniq([
  ...DateTermCardTerms,
  ...GeoTerms,
  ...PivotCardPivotTerms,
  ...TermCardTerms,
  ...TermMetricsCardTerms,
  Facet.ContentClassification,
]);

export const hideableTerms = [
  Facet.Affects,
  Facet.Hashtags,
  Facet.SignificantHashtags,
  Facet.EmergingHashtags,
  Facet.Mentions,
  Facet.Entities,
  Facet.Keyphrases,
  Facet.SignificantMentions,
  Facet.SignificantKeyphrases,
  Facet.EmergingKeyphrases,
  Facet.EmergingMentions,
  Facet.EmergingNamedEntities,
  Facet.SignificantNamedEntities,
];

export type CardTerm =
  | DateTermCardTerm
  | GeoCardTerm
  | PivotCardTerm
  | TermCardTerm
  | TermMetricsCardTerm
  | Facet.ContentClassification;

function isClosedCustomField(facet: FullFacet) {
  return facet && facet.custom && facet.type === "closed";
}

function isOpenedCustomField(facet: FullFacet) {
  return facet && facet.custom && facet.type === "opened";
}

export function getTermFromCard(card: Card) {
  if (
    isTermCard(card) ||
    isGeoCard(card) ||
    isClassificationCard(card) ||
    isTermMetricsCard(card) ||
    isPivotCard(card)
  ) {
    const fragment = card.fragments[0];
    return getTermFromAggs(fragment.request.aggs) as Facet;
  } else if (isDateTermCard(card)) {
    const fragment = card.fragments[0];
    return getTermFromAggs(fragment.request.aggs.date.aggs) as Facet;
  } else if (isDemographicCard(card)) {
    return demographicId;
  } else if (isDateMetricCard(card) || isDateMetricsCard(card)) {
    return globalVolumeAndKPIId;
  } else {
    return null;
  }
}

export function getTermsFromPivotCard(card: Card) {
  if (isPivotCard(card)) {
    const fragment = card.fragments[0];
    const pivot = getTermFromAggs(fragment.request.aggs);
    const against = getTermFromAggs(fragment.request.aggs[pivot].aggs);
    return { pivot, against };
  }
  return { pivot: null, against: null };
}

export function getValidTermCardTerm(term: Facet) {
  if (!isTermCardTerm(term)) {
    return Facet.Platforms;
  } else {
    return term;
  }
}

export function isTermCardTerm(
  term: Facet,
  facet?: FullFacet
): term is TermCardTerm {
  if (facet && facet.custom) {
    return true;
  }
  return includes(TermCardTerms, term);
}

export function isDateTermCardTerm(
  term: Facet,
  facet: FullFacet
): term is DateTermCardTerm {
  if (isClosedCustomField(facet)) {
    return true;
  }
  return includes(DateTermCardTerms, term);
}

export function isTermMetricsCardTerm(
  term: Facet,
  facet: FullFacet
): term is TermMetricsCardTerm {
  if (isClosedCustomField(facet)) {
    return true;
  }
  return includes(TermMetricsCardTerms, term);
}

export function isPivotCardTerm(
  term: Facet,
  facet: FullFacet
): term is PivotCardTerm {
  if (isClosedCustomField(facet)) {
    return true;
  }
  return includes(PivotCardPivotTerms, term);
}

export function isCardTerm(term: Facet): term is CardTerm {
  return includes(CardTerms, term);
}

// Terms that only use a segment of the data instead of aggregating
export function isSampleTerm(term: Facet, facet?: FullFacet) {
  if (facet && isOpenedCustomField(facet)) {
    return true;
  }
  return includes(
    [
      Facet.Affects,
      Facet.EmojiCharts,
      Facet.Hashtags,
      Facet.Mentions,
      Facet.Entities,
      Facet.ImageRecognitionObjects,
      Facet.ImageRecognitionScenes,
      Facet.Occupations,
      Facet.Topics,
      Facet.Product,
      Facet.ProductRange,
      Facet.Franchise,
      Facet.SignificantEntities,
      Facet.SignificantHashtags,
      Facet.SignificantMentions,
      Facet.SignificantNamedEntities,
      Facet.EmergingHashtags,
      Facet.EmergingMentions,
      Facet.EmergingNamedEntities,
      Facet.ImageRecognitionLogos,
      Facet.ImageRecognitionPersonIdentifications,
    ],
    term
  );
}

export function isTermCompatibleForCard(
  card: Card,
  term: Facet,
  facet: FullFacet
) {
  if (isGeoCard(card)) {
    return isGeoTerm(term);
  } else if (isDateTermCard(card)) {
    return isDateTermCardTerm(term, facet);
  } else if (isTermMetricsCard(card)) {
    return isTermMetricsCardTerm(term, facet);
  } else if (isPivotCard(card)) {
    return isPivotCardTerm(term, facet);
  } else if (isTermCard(card)) {
    return isTermCardTerm(term);
  } else {
    return false;
  }
}
